import { DebouncedOptions, S25Util } from "../util/s25-util";

/**
 * Make a function (typically method) debounced by a number of milliseconds.
 * The function gains a `.flush()` method that can be used in unit tests to skip
 * any pending delay. e.g.
 * ```ts
 * class MyComponent {
 *   @Debounce(500)
 *   debouncedMethod() { ... }
 * }
 *
 * it("should handle click", () => {
 *   componentFixture.find("button").click();
 *   // you DON'T have to wait 500ms, if you flush
 *   componentInstance.debouncedMethod.flush();
 *   expect(...itDidTheThing);
 * });
 * ```
 */
export function Debounce(ms?: number, options?: DebouncedOptions<any>) {
    return function (target: any, propertyKey: string, descriptor: PropertyDescriptor) {
        if (typeof descriptor.value !== "function") {
            throw new Error(`@Debounce() can only be used on methods. ${propertyKey} is not a method.`);
        }
        const original = descriptor.value;
        delete descriptor.value;
        delete descriptor.writable;
        const memo = new WeakMap();
        descriptor.get = function () {
            if (!memo.has(this)) memo.set(this, S25Util.debounced(original.bind(this), ms, options));
            return memo.get(this);
        };
        return descriptor;
    };
}

export namespace Debounce {
    /**
     * Skip the debounce delay and immediately run the last invocation of the debounced function
     */
    export function flush(debouncedFn: (...args: unknown[]) => unknown) {
        if ("flush" in debouncedFn) {
            (<any>debouncedFn).flush();
        } else {
            throw new Error("Tried to flush a non-debounced function");
        }
    }
}

// Make a function (typically method) throttled by a number of millisecond
// debounceLast=true: the last call is set to be executed once it's been "ms" ms
// debounceLast=false: if it has not yet been "ms" ms, do nothing
export function Throttle(ms?: number, debounceLast = true) {
    return function (target: any, propertyKey: string, descriptor: PropertyDescriptor) {
        if (typeof descriptor.value !== "function") {
            throw new Error(`@Throttle() can only be used on methods. ${propertyKey} is not a method.`);
        }
        const original = descriptor.value;
        delete descriptor.value;
        delete descriptor.writable;
        const memo = new WeakMap();
        descriptor.get = function () {
            if (!memo.has(this)) memo.set(this, S25Util.throttled(original.bind(this), ms, debounceLast));
            return memo.get(this);
        };
        return descriptor;
    };
}
